'use client'

import {
  type EndCondition,
  type IsoWeekday,
  type MonthlyMode,
  normalizeRule,
  type NthWeekdayPosition,
  type RecurrenceFrequency,
  type RecurrenceRule,
} from '@ordo/recurrence'
import { Calendar as CalendarIcon, Clock, Plus, Repeat, X } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { cn } from '../cn'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Separator } from './separator'

export interface DateRecurrencePickerProps {
  dueDate: Date | null
  startDate: Date | null
  hasDueTime: boolean
  /** Optional. When omitted, defaults to false. */
  hasStartTime?: boolean
  recurrenceRule: RecurrenceRule | null
  onDueDateChange: (date: Date | null) => void
  onStartDateChange: (date: Date | null) => void
  onHasDueTimeChange: (has: boolean) => void
  /** Optional. When the picker is used without start-time semantics, omit this. */
  onHasStartTimeChange?: (has: boolean) => void
  onRecurrenceChange: (rule: RecurrenceRule | null) => void
  disabled?: boolean
  /** Keep the due date present; start dates and repeat settings remain editable. */
  dueDateRequired?: boolean
  /** Custom trigger element — rendered via `asChild`, so must accept a ref and onClick. */
  trigger?: ReactNode
  /** Hide the leading dashed-circle calendar icon on the DEFAULT trigger (e.g. the
   *  detail-panel properties grid, where the row already has a label-column icon).
   *  No effect when a custom `trigger` is supplied. Defaults to false. */
  hideTriggerIcon?: boolean
}

type PresetOption = RecurrenceFrequency | 'none' | 'custom'

const FREQUENCY_OPTIONS: { value: PresetOption; label: string }[] = [
  { value: 'none', label: 'No repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'custom', label: 'Custom' },
]

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const INDEX_TO_ISO: IsoWeekday[] = [7, 1, 2, 3, 4, 5, 6]

const NTH_POSITIONS: { value: NthWeekdayPosition; label: string }[] = [
  { value: 1, label: 'first' },
  { value: 2, label: 'second' },
  { value: 3, label: 'third' },
  { value: 4, label: 'fourth' },
  { value: -1, label: 'last' },
]

const NTH_WEEKDAY_OPTIONS: { value: IsoWeekday; label: string }[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
]

function isSimplePreset(rule: RecurrenceRule): boolean {
  return (
    rule.interval === 1 &&
    !rule.weekDays?.length &&
    !rule.monthDay &&
    !rule.nthWeekday &&
    !rule.periodic &&
    (!rule.endCondition || rule.endCondition.kind === 'never')
  )
}

function ruleToSelectValue(rule: RecurrenceRule | null): PresetOption {
  if (!rule) {
    return 'none'
  }
  if (isSimplePreset(rule)) {
    return rule.frequency
  }
  return 'custom'
}

function toTimeValue(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function mergeDateAndTime(date: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)
  const merged = new Date(date)
  merged.setHours(hours, minutes, 0, 0)
  return merged
}

function toDateInputValue(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const y = String(date.getFullYear()).slice(-2)
  return `${m}/${d}/${y}`
}

function parseDateInput(value: string): Date | null {
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (!match) {
    return null
  }
  const month = parseInt(match[1], 10)
  const day = parseInt(match[2], 10)
  let year = parseInt(match[3], 10)
  if (year < 100) {
    year += 2000
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null
  }
  const date = new Date(year, month - 1, day)
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null
  }
  return date
}

function formatMonthDay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatFull(date: Date, withYear: boolean): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  })
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatDateRange(
  startDate: Date | null,
  dueDate: Date | null,
  hasDueTime: boolean,
  hasStartTime: boolean,
): string {
  if (!startDate && !dueDate) {
    return 'No due date'
  }
  const now = new Date()
  const sameYear = (d: Date) => d.getFullYear() === now.getFullYear()

  if (startDate && dueDate) {
    const startLabel = `${formatFull(startDate, !sameYear(startDate))}${hasStartTime ? `, ${formatTime(startDate)}` : ''}`
    const dueLabel = `${formatFull(dueDate, !sameYear(dueDate))}${hasDueTime ? `, ${formatTime(dueDate)}` : ''}`
    return `${startLabel} – ${dueLabel}`
  }
  if (dueDate) {
    return `${formatFull(dueDate, !sameYear(dueDate))}${hasDueTime ? `, ${formatTime(dueDate)}` : ''}`
  }
  return `Starting ${formatMonthDay(startDate!)}${hasStartTime ? `, ${formatTime(startDate!)}` : ''}`
}

function deriveNthWeekdayFromDate(date: Date): { position: NthWeekdayPosition; weekday: IsoWeekday } {
  const weekday = ((date.getDay() + 6) % 7) + 1 // JS 0=Sun..6=Sat → ISO 1=Mon..7=Sun
  const position = Math.min(4, Math.ceil(date.getDate() / 7)) as NthWeekdayPosition
  return { position, weekday: weekday as IsoWeekday }
}

export interface RecurrenceDetailProps {
  value: RecurrenceRule | null
  onChange: (rule: RecurrenceRule | null) => void
  /** Used to seed monthly day-of-month / nth-weekday defaults. */
  anchorDate?: Date | null
  /** Hide the "After completion (instead of on a schedule)" checkbox.
   *  Set true on surfaces where there's no user-completion event to
   *  re-anchor from — e.g. cron-driven automations. Default false
   *  preserves the Tasker task-picker behavior. */
  hideAfterCompletion?: boolean
}

/**
 * Standalone recurrence-detail panel — same visual layout as the
 * custom branch inside `DateRecurrencePicker`'s popover, but mounted
 * inline by callers (e.g. the agent Automations form) without the
 * surrounding popover wrapper.
 */
export function RecurrenceDetail({ value, onChange, anchorDate, hideAfterCompletion }: RecurrenceDetailProps) {
  const normalizedRule = useMemo(() => (value ? normalizeRule(value) : null), [value])

  const [customFrequency, setCustomFrequency] = useState<RecurrenceFrequency>(normalizedRule?.frequency ?? 'weekly')
  const [customInterval, setCustomInterval] = useState(normalizedRule?.interval ?? 1)
  const [customWeekDays, setCustomWeekDays] = useState<IsoWeekday[]>(normalizedRule?.weekDays ?? [])
  const [customMonthDay, setCustomMonthDay] = useState<number>(
    normalizedRule?.monthDay ?? (anchorDate ? anchorDate.getDate() : 1),
  )
  const [customMonthlyMode, setCustomMonthlyMode] = useState<MonthlyMode>(normalizedRule?.monthlyMode ?? 'day-of-month')
  const [customNthPosition, setCustomNthPosition] = useState<NthWeekdayPosition>(
    normalizedRule?.nthWeekday?.position ?? (anchorDate ? deriveNthWeekdayFromDate(anchorDate).position : 1),
  )
  const [customNthWeekday, setCustomNthWeekday] = useState<IsoWeekday>(
    normalizedRule?.nthWeekday?.weekday ?? (anchorDate ? deriveNthWeekdayFromDate(anchorDate).weekday : 1),
  )
  const [customPeriodic, setCustomPeriodic] = useState<boolean>(normalizedRule?.periodic ?? false)
  const [customEndKind, setCustomEndKind] = useState<EndCondition['kind']>(
    normalizedRule?.endCondition?.kind ?? 'never',
  )
  const [customEndDate, setCustomEndDate] = useState<string>(
    normalizedRule?.endCondition?.kind === 'on' ? normalizedRule.endCondition.date.slice(0, 10) : '',
  )
  const [customEndAfter, setCustomEndAfter] = useState<number>(
    normalizedRule?.endCondition?.kind === 'after' ? normalizedRule.endCondition.count : 5,
  )

  const buildRule = useCallback((): RecurrenceRule => {
    const rule: RecurrenceRule = { frequency: customFrequency, interval: customInterval }
    if (customFrequency === 'weekly' && customWeekDays.length > 0) {
      rule.weekDays = [...customWeekDays].sort((a, b) => a - b) as IsoWeekday[]
    }
    if (customFrequency === 'monthly') {
      rule.monthlyMode = customMonthlyMode
      if (customMonthlyMode === 'day-of-month') {
        rule.monthDay = customMonthDay
      } else {
        rule.nthWeekday = { position: customNthPosition, weekday: customNthWeekday }
      }
    }
    if (customPeriodic) {
      rule.periodic = true
    }
    if (customEndKind === 'after') {
      rule.endCondition = { kind: 'after', count: Math.max(1, customEndAfter) }
    } else if (customEndKind === 'on' && customEndDate) {
      const parsed = new Date(customEndDate)
      rule.endCondition = Number.isNaN(parsed.getTime())
        ? { kind: 'never' }
        : { kind: 'on', date: parsed.toISOString() }
    } else {
      rule.endCondition = { kind: 'never' }
    }
    return rule
  }, [
    customFrequency,
    customInterval,
    customWeekDays,
    customMonthlyMode,
    customMonthDay,
    customNthPosition,
    customNthWeekday,
    customPeriodic,
    customEndKind,
    customEndAfter,
    customEndDate,
  ])

  const apply = useCallback(() => onChange(buildRule()), [buildRule, onChange])

  // Skip the initial render (state was just seeded from `value` —
  // emitting now would loop). Emit on every subsequent state change.
  const didInitRef = useRef(false)
  useEffect(() => {
    if (!didInitRef.current) {
      didInitRef.current = true
      return
    }
    onChange(buildRule())
  }, [buildRule, onChange])

  const toggleWeekDay = useCallback((isoDay: IsoWeekday) => {
    setCustomWeekDays(
      (prev) =>
        (prev.includes(isoDay)
          ? prev.filter((d) => d !== isoDay)
          : [...prev, isoDay].sort((a, b) => a - b)) as IsoWeekday[],
    )
  }, [])

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">Every</span>
        <input
          type="number"
          min={1}
          max={99}
          value={customInterval}
          onChange={(e) => setCustomInterval(Math.max(1, parseInt(e.target.value) || 1))}
          onBlur={apply}
          inputMode="numeric"
          className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40"
        />
        <select
          value={customFrequency}
          onChange={(e) => setCustomFrequency(e.target.value as RecurrenceFrequency)}
          className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40 cursor-pointer">
          <option value="daily">Days</option>
          <option value="weekly">Weeks</option>
          <option value="monthly">Months</option>
          <option value="yearly">Years</option>
        </select>
      </div>

      {hideAfterCompletion ? null : (
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={customPeriodic}
            onChange={(e) => setCustomPeriodic(e.target.checked)}
            className="accent-primary"
          />
          <span>After completion (instead of on a schedule)</span>
        </label>
      )}

      {customFrequency === 'weekly' && !customPeriodic ? (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-muted-foreground">On weekday</span>
          <div className="flex gap-1 justify-start">
            {WEEKDAY_LABELS.map((label, idx) => {
              const isoDay = INDEX_TO_ISO[idx]!
              const active = customWeekDays.includes(isoDay)
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => toggleWeekDay(isoDay)}
                  className={cn(
                    'h-8 w-8 rounded-full text-xs font-medium transition-colors',
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80',
                  )}>
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {customFrequency === 'monthly' && !customPeriodic ? (
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="rd-monthly-mode"
              checked={customMonthlyMode === 'day-of-month'}
              onChange={() => setCustomMonthlyMode('day-of-month')}
              className="accent-primary"
            />
            <span className="text-muted-foreground">On day</span>
            <input
              type="number"
              min={1}
              max={31}
              value={customMonthDay}
              disabled={customMonthlyMode !== 'day-of-month'}
              onChange={(e) => setCustomMonthDay(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
              onBlur={apply}
              inputMode="numeric"
              className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40 disabled:opacity-50"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer flex-wrap">
            <input
              type="radio"
              name="rd-monthly-mode"
              checked={customMonthlyMode === 'nth-weekday'}
              onChange={() => setCustomMonthlyMode('nth-weekday')}
              className="accent-primary"
            />
            <span className="text-muted-foreground">On the</span>
            <select
              value={customNthPosition}
              disabled={customMonthlyMode !== 'nth-weekday'}
              onChange={(e) => setCustomNthPosition(parseInt(e.target.value) as NthWeekdayPosition)}
              className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none cursor-pointer disabled:opacity-50">
              {NTH_POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <select
              value={customNthWeekday}
              disabled={customMonthlyMode !== 'nth-weekday'}
              onChange={(e) => setCustomNthWeekday(parseInt(e.target.value) as IsoWeekday)}
              className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none cursor-pointer disabled:opacity-50">
              {NTH_WEEKDAY_OPTIONS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">Ends</span>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="rd-end-kind"
            checked={customEndKind === 'never'}
            onChange={() => setCustomEndKind('never')}
            className="accent-primary"
          />
          <span className="w-14">Never</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="rd-end-kind"
            checked={customEndKind === 'on'}
            onChange={() => setCustomEndKind('on')}
            className="accent-primary"
          />
          <span className="w-14 text-muted-foreground">On</span>
          <input
            type="date"
            value={customEndDate}
            disabled={customEndKind !== 'on'}
            onChange={(e) => setCustomEndDate(e.target.value)}
            onBlur={apply}
            className="text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40 disabled:opacity-50"
          />
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="rd-end-kind"
            checked={customEndKind === 'after'}
            onChange={() => setCustomEndKind('after')}
            className="accent-primary"
          />
          <span className="w-14 text-muted-foreground">After</span>
          <input
            type="number"
            min={1}
            max={999}
            value={customEndAfter}
            disabled={customEndKind !== 'after'}
            onChange={(e) => setCustomEndAfter(Math.max(1, parseInt(e.target.value) || 1))}
            onBlur={apply}
            inputMode="numeric"
            className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40 disabled:opacity-50"
          />
          <span className="text-muted-foreground">occurrences</span>
        </label>
      </div>
    </div>
  )
}

export function DateRecurrencePicker({
  dueDate,
  startDate,
  hasDueTime,
  hasStartTime: hasStartTimeProp,
  recurrenceRule,
  onDueDateChange,
  onStartDateChange,
  onHasDueTimeChange,
  onHasStartTimeChange,
  onRecurrenceChange,
  disabled,
  dueDateRequired = false,
  trigger,
  hideTriggerIcon = false,
}: DateRecurrencePickerProps) {
  const hasStartTime = hasStartTimeProp ?? false
  const normalizedRule = useMemo(() => (recurrenceRule ? normalizeRule(recurrenceRule) : null), [recurrenceRule])

  const [open, setOpen] = useState(false)
  const [selecting, setSelecting] = useState<'start' | 'due'>('due')
  const [showStartInput, setShowStartInput] = useState(!!startDate)
  const [timeValue, setTimeValue] = useState(dueDate && hasDueTime ? toTimeValue(dueDate) : '09:00')
  const [startTimeValue, setStartTimeValue] = useState(startDate && hasStartTime ? toTimeValue(startDate) : '09:00')

  const [editingField, setEditingField] = useState<'start' | 'due' | null>(null)
  const [editBuffer, setEditBuffer] = useState('')

  const startDateDisplay = startDate ? toDateInputValue(startDate) : ''
  const dueDateDisplay = dueDate ? toDateInputValue(dueDate) : ''

  const [showCustom, setShowCustom] = useState(ruleToSelectValue(normalizedRule) === 'custom')
  const [customFrequency, setCustomFrequency] = useState<RecurrenceFrequency>(normalizedRule?.frequency ?? 'weekly')
  const [customInterval, setCustomInterval] = useState(normalizedRule?.interval ?? 1)
  const [customWeekDays, setCustomWeekDays] = useState<IsoWeekday[]>(normalizedRule?.weekDays ?? [])
  const [customMonthDay, setCustomMonthDay] = useState<number>(
    normalizedRule?.monthDay ?? (dueDate ? dueDate.getDate() : 1),
  )
  const [customMonthlyMode, setCustomMonthlyMode] = useState<MonthlyMode>(normalizedRule?.monthlyMode ?? 'day-of-month')
  const [customNthPosition, setCustomNthPosition] = useState<NthWeekdayPosition>(
    normalizedRule?.nthWeekday?.position ?? (dueDate ? deriveNthWeekdayFromDate(dueDate).position : 1),
  )
  const [customNthWeekday, setCustomNthWeekday] = useState<IsoWeekday>(
    normalizedRule?.nthWeekday?.weekday ?? (dueDate ? deriveNthWeekdayFromDate(dueDate).weekday : 1),
  )
  const [customPeriodic, setCustomPeriodic] = useState<boolean>(normalizedRule?.periodic ?? false)
  const [customEndKind, setCustomEndKind] = useState<EndCondition['kind']>(
    normalizedRule?.endCondition?.kind ?? 'never',
  )
  const [customEndDate, setCustomEndDate] = useState<string>(
    normalizedRule?.endCondition?.kind === 'on' ? normalizedRule.endCondition.date.slice(0, 10) : '',
  )
  const [customEndAfter, setCustomEndAfter] = useState<number>(
    normalizedRule?.endCondition?.kind === 'after' ? normalizedRule.endCondition.count : 5,
  )

  const startInputRef = useRef<HTMLInputElement>(null)
  const dueInputRef = useRef<HTMLInputElement>(null)

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen)
      if (nextOpen) {
        setSelecting('due')
        setShowStartInput(!!startDate)
        setEditingField(null)
      }
    },
    [startDate],
  )

  const handleCalendarSelect = useCallback(
    (selected: Date | undefined) => {
      if (!selected) {
        return
      }
      setEditingField(null)
      if (selecting === 'start') {
        const clamped =
          dueDate && selected > dueDate
            ? new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate())
            : selected
        const dateToSet = hasStartTime ? mergeDateAndTime(clamped, startTimeValue) : clamped
        onStartDateChange(dateToSet)
      } else {
        const clamped = startDate && selected < startDate ? startDate : selected
        const dateToSet = hasDueTime ? mergeDateAndTime(clamped, timeValue) : clamped
        onDueDateChange(dateToSet)
      }
    },
    [
      selecting,
      dueDate,
      startDate,
      hasDueTime,
      hasStartTime,
      timeValue,
      startTimeValue,
      onStartDateChange,
      onDueDateChange,
    ],
  )

  const commitEditBuffer = useCallback(
    (field: 'start' | 'due') => {
      const text = editBuffer.trim()
      setEditingField(null)
      if (!text) {
        if (field === 'start') {
          onStartDateChange(null)
        } else if (!dueDateRequired) {
          onDueDateChange(null)
        }
        return
      }
      const parsed = parseDateInput(text)
      if (!parsed) {
        return
      }
      if (field === 'start') {
        const clamped =
          dueDate && parsed > dueDate ? new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) : parsed
        const dateToSet = hasStartTime ? mergeDateAndTime(clamped, startTimeValue) : clamped
        onStartDateChange(dateToSet)
      } else {
        const clamped = startDate && parsed < startDate ? startDate : parsed
        const dateToSet = hasDueTime ? mergeDateAndTime(clamped, timeValue) : clamped
        onDueDateChange(dateToSet)
      }
    },
    [
      editBuffer,
      dueDateRequired,
      dueDate,
      startDate,
      hasDueTime,
      hasStartTime,
      timeValue,
      startTimeValue,
      onStartDateChange,
      onDueDateChange,
    ],
  )

  const handleTimeChange = useCallback(
    (time: string) => {
      setTimeValue(time)
      if (dueDate) {
        onDueDateChange(mergeDateAndTime(dueDate, time))
      }
    },
    [dueDate, onDueDateChange],
  )

  const handleStartTimeChange = useCallback(
    (time: string) => {
      setStartTimeValue(time)
      if (startDate) {
        onStartDateChange(mergeDateAndTime(startDate, time))
      }
    },
    [startDate, onStartDateChange],
  )

  const handleToggleStartTime = useCallback(() => {
    if (!onHasStartTimeChange) {
      return
    }
    if (hasStartTime) {
      onHasStartTimeChange(false)
      setStartTimeValue('09:00')
      if (startDate) {
        const dateOnly = new Date(startDate)
        dateOnly.setHours(0, 0, 0, 0)
        onStartDateChange(dateOnly)
      }
    } else {
      const time = '09:00'
      setStartTimeValue(time)
      onHasStartTimeChange(true)
      if (startDate) {
        onStartDateChange(mergeDateAndTime(startDate, time))
      }
    }
  }, [hasStartTime, startDate, onStartDateChange, onHasStartTimeChange])

  const handleToggleTime = useCallback(() => {
    if (hasDueTime) {
      onHasDueTimeChange(false)
      setTimeValue('09:00')
      if (dueDate) {
        const dateOnly = new Date(dueDate)
        dateOnly.setHours(0, 0, 0, 0)
        onDueDateChange(dateOnly)
      }
    } else {
      const time = '09:00'
      setTimeValue(time)
      onHasDueTimeChange(true)
      if (dueDate) {
        onDueDateChange(mergeDateAndTime(dueDate, time))
      }
    }
  }, [hasDueTime, dueDate, onDueDateChange, onHasDueTimeChange])

  const handleAddStartDate = useCallback(() => {
    setShowStartInput(true)
    setSelecting('start')
    setTimeout(() => startInputRef.current?.focus(), 0)
  }, [])

  const handleClearStartDate = useCallback(() => {
    onStartDateChange(null)
    setEditingField(null)
    setShowStartInput(false)
    setSelecting('due')
  }, [onStartDateChange])

  const handleClearDueDate = useCallback(() => {
    if (dueDateRequired) {
      return
    }
    onDueDateChange(null)
    setEditingField(null)
    onHasDueTimeChange(false)
    setTimeValue('09:00')
  }, [dueDateRequired, onDueDateChange, onHasDueTimeChange])

  const buildCustomRule = useCallback((): RecurrenceRule => {
    const rule: RecurrenceRule = {
      frequency: customFrequency,
      interval: customInterval,
    }
    if (customFrequency === 'weekly' && customWeekDays.length > 0) {
      rule.weekDays = [...customWeekDays].sort((a, b) => a - b) as IsoWeekday[]
    }
    if (customFrequency === 'monthly') {
      rule.monthlyMode = customMonthlyMode
      if (customMonthlyMode === 'day-of-month') {
        rule.monthDay = customMonthDay
      } else {
        rule.nthWeekday = { position: customNthPosition, weekday: customNthWeekday }
      }
    }
    if (customPeriodic) {
      rule.periodic = true
    }
    if (customEndKind === 'after') {
      rule.endCondition = { kind: 'after', count: Math.max(1, customEndAfter) }
    } else if (customEndKind === 'on' && customEndDate) {
      const parsed = new Date(customEndDate)
      if (Number.isNaN(parsed.getTime())) {
        rule.endCondition = { kind: 'never' }
      } else {
        rule.endCondition = { kind: 'on', date: parsed.toISOString() }
      }
    } else {
      rule.endCondition = { kind: 'never' }
    }
    return rule
  }, [
    customFrequency,
    customInterval,
    customWeekDays,
    customMonthlyMode,
    customMonthDay,
    customNthPosition,
    customNthWeekday,
    customPeriodic,
    customEndKind,
    customEndAfter,
    customEndDate,
  ])

  const applyCustomRule = useCallback(() => {
    onRecurrenceChange(buildCustomRule())
  }, [buildCustomRule, onRecurrenceChange])

  // Emit the rule whenever any custom* state changes. Driven by an effect so
  // fresh state is always captured — the previous `setTimeout(applyCustomRule, 0)`
  // pattern in every control's onClick fired with a stale closure and dropped
  // whichever field the user touched most recently.
  const didSeedCustomRef = useRef(false)
  useEffect(() => {
    if (!showCustom) {
      didSeedCustomRef.current = false
      return
    }
    if (!didSeedCustomRef.current) {
      didSeedCustomRef.current = true
      return
    }
    onRecurrenceChange(buildCustomRule())
  }, [showCustom, buildCustomRule, onRecurrenceChange])

  const handlePresetChange = useCallback(
    (value: string) => {
      if (value === 'none') {
        onRecurrenceChange(null)
        setShowCustom(false)
        return
      }
      if (value === 'custom') {
        setShowCustom(true)
        setCustomFrequency(normalizedRule?.frequency ?? 'weekly')
        setCustomInterval(normalizedRule?.interval ?? 1)
        setCustomWeekDays(normalizedRule?.weekDays ?? [])
        setCustomMonthDay(normalizedRule?.monthDay ?? (dueDate ? dueDate.getDate() : 1))
        setCustomMonthlyMode(normalizedRule?.monthlyMode ?? 'day-of-month')
        setCustomPeriodic(normalizedRule?.periodic ?? false)
        setCustomEndKind(normalizedRule?.endCondition?.kind ?? 'never')
        return
      }
      setShowCustom(false)
      onRecurrenceChange({ frequency: value as RecurrenceFrequency, interval: 1, endCondition: { kind: 'never' } })
    },
    [onRecurrenceChange, normalizedRule, dueDate],
  )

  const toggleWeekDay = useCallback((isoDay: IsoWeekday) => {
    setCustomWeekDays(
      (prev) =>
        (prev.includes(isoDay)
          ? prev.filter((d) => d !== isoDay)
          : [...prev, isoDay].sort((a, b) => a - b)) as IsoWeekday[],
    )
  }, [])

  const handleClear = useCallback(() => {
    if (dueDateRequired) {
      return
    }
    onDueDateChange(null)
    onStartDateChange(null)
    onHasDueTimeChange(false)
    onRecurrenceChange(null)
    setShowCustom(false)
    setTimeValue('09:00')
    setEditingField(null)
    setShowStartInput(false)
    setOpen(false)
  }, [dueDateRequired, onDueDateChange, onStartDateChange, onHasDueTimeChange, onRecurrenceChange])

  const triggerLabel = formatDateRange(startDate, dueDate, hasDueTime, hasStartTime)
  const selectValue = ruleToSelectValue(normalizedRule)
  const hasAnyDate = !!startDate || !!dueDate

  const activeDate = selecting === 'start' ? startDate : dueDate
  const otherDate = selecting === 'start' ? dueDate : startDate
  const calendarModifiers: Record<string, Date | { after: Date; before: Date }> = {}
  if (otherDate) {
    calendarModifiers.otherDate = otherDate
  }
  if (startDate && dueDate && startDate < dueDate) {
    calendarModifiers.inRange = { after: startDate, before: dueDate }
  }
  const calendarMonth = activeDate ?? (selecting === 'start' ? dueDate : startDate) ?? undefined

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {trigger ? (
        <PopoverTrigger disabled={disabled} className="appearance-none bg-transparent border-0 p-0 m-0 leading-none">
          {trigger}
        </PopoverTrigger>
      ) : (
        <div className="inline-flex items-center gap-1.5">
          <PopoverTrigger
            disabled={disabled}
            className={cn(
              'inline-flex items-center gap-2 text-sm transition-colors hover:text-foreground/80 cursor-pointer py-0.5',
              !hasAnyDate && 'text-muted-foreground',
            )}>
            {!hideTriggerIcon && (
              <span className="flex items-center justify-center h-6 w-6 rounded-full border border-dashed !border-foreground/40">
                <CalendarIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <span>{triggerLabel}</span>
            {normalizedRule ? <Repeat className="h-3 w-3 text-primary" /> : null}
          </PopoverTrigger>
          {hasAnyDate && !disabled && !dueDateRequired ? (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear dates"
              className="text-muted-foreground hover:text-foreground transition-colors p-0.5">
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      )}

      <PopoverContent className="w-[340px] p-0" align="start">
        {/* Date input row */}
        <div className="flex items-center gap-2 px-3 py-2.5">
          {showStartInput ? (
            <div
              className={cn(
                'flex items-center gap-1 rounded-md border px-2 py-1 text-sm flex-1 min-w-0 cursor-text transition-colors',
                selecting === 'start' ? 'border-primary ring-1 ring-primary/30' : 'border-border',
              )}
              onClick={() => {
                setSelecting('start')
                startInputRef.current?.focus()
              }}>
              <input
                ref={startInputRef}
                type="text"
                placeholder="Start date"
                value={editingField === 'start' ? editBuffer : startDateDisplay}
                onChange={(e) => setEditBuffer(e.target.value)}
                onBlur={() => commitEditBuffer('start')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitEditBuffer('start')
                  }
                }}
                onFocus={() => {
                  setSelecting('start')
                  setEditingField('start')
                  setEditBuffer(startDateDisplay)
                }}
                className="bg-transparent outline-none w-full min-w-0 text-sm pointer-coarse:text-base"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClearStartDate()
                }}
                className="shrink-0 rounded p-0.5 hover:bg-muted transition-colors">
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleAddStartDate}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Plus className="h-3.5 w-3.5" />
              <span>Start date</span>
            </button>
          )}

          <div
            className={cn(
              'flex items-center gap-1 rounded-md border px-2 py-1 text-sm flex-1 min-w-0 cursor-text transition-colors',
              selecting === 'due' ? 'border-primary ring-1 ring-primary/30' : 'border-border',
            )}
            onClick={() => {
              setSelecting('due')
              dueInputRef.current?.focus()
            }}>
            <input
              ref={dueInputRef}
              type="text"
              placeholder="Due date"
              value={editingField === 'due' ? editBuffer : dueDateDisplay}
              onChange={(e) => setEditBuffer(e.target.value)}
              onBlur={() => commitEditBuffer('due')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitEditBuffer('due')
                }
              }}
              onFocus={() => {
                setSelecting('due')
                setEditingField('due')
                setEditBuffer(dueDateDisplay)
              }}
              className="bg-transparent outline-none w-full min-w-0 text-sm pointer-coarse:text-base"
            />
            {dueDate && !dueDateRequired ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleClearDueDate()
                }}
                className="shrink-0 rounded p-0.5 hover:bg-muted transition-colors">
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            ) : null}
          </div>
        </div>

        <Separator />

        <Calendar
          mode="single"
          selected={activeDate ?? undefined}
          onSelect={handleCalendarSelect}
          defaultMonth={calendarMonth}
          modifiers={calendarModifiers}
          modifiersClassNames={{
            otherDate: '[&_button]:bg-primary/20 [&_button]:text-foreground [&_button]:rounded-md',
            inRange: 'bg-primary/5',
          }}
        />

        <Separator />

        {/* Recurrence section */}
        <div className="px-3 py-2.5 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Repeats</span>
            <select
              value={showCustom ? 'custom' : selectValue}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40 cursor-pointer">
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {showCustom ? (
            <div className="space-y-3">
              {/* Every N [frequency] + after-completion toggle */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Every</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={customInterval}
                      onChange={(e) => setCustomInterval(Math.max(1, parseInt(e.target.value) || 1))}
                      onBlur={applyCustomRule}
                      inputMode="numeric"
                      className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40"
                    />
                    <select
                      value={customFrequency}
                      onChange={(e) => setCustomFrequency(e.target.value as RecurrenceFrequency)}
                      className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40 cursor-pointer">
                      <option value="daily">Days</option>
                      <option value="weekly">Weeks</option>
                      <option value="monthly">Months</option>
                      <option value="yearly">Years</option>
                    </select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customPeriodic}
                    onChange={(e) => setCustomPeriodic(e.target.checked)}
                    className="accent-primary"
                  />
                  <span>After completion (instead of on a schedule)</span>
                </label>
              </div>

              {/* Weekly: day-of-week toggles */}
              {customFrequency === 'weekly' && !customPeriodic ? (
                <div className="flex gap-1 justify-center">
                  {WEEKDAY_LABELS.map((label, idx) => {
                    const isoDay = INDEX_TO_ISO[idx]
                    const active = customWeekDays.includes(isoDay)
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleWeekDay(isoDay)}
                        className={cn(
                          'h-8 w-8 rounded-full text-xs font-medium transition-colors',
                          active
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80',
                        )}>
                        {label}
                      </button>
                    )
                  })}
                </div>
              ) : null}

              {/* Monthly: mode toggle + inputs */}
              {customFrequency === 'monthly' && !customPeriodic ? (
                <div className="space-y-2 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="monthly-mode"
                      checked={customMonthlyMode === 'day-of-month'}
                      onChange={() => setCustomMonthlyMode('day-of-month')}
                      className="accent-primary"
                    />
                    <span className="text-muted-foreground">On day</span>
                    <input
                      type="number"
                      min={1}
                      max={31}
                      value={customMonthDay}
                      disabled={customMonthlyMode !== 'day-of-month'}
                      onChange={(e) => setCustomMonthDay(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
                      onBlur={applyCustomRule}
                      inputMode="numeric"
                      className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40 disabled:opacity-50"
                    />
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer flex-wrap">
                    <input
                      type="radio"
                      name="monthly-mode"
                      checked={customMonthlyMode === 'nth-weekday'}
                      onChange={() => setCustomMonthlyMode('nth-weekday')}
                      className="accent-primary"
                    />
                    <span className="text-muted-foreground">On the</span>
                    <select
                      value={customNthPosition}
                      disabled={customMonthlyMode !== 'nth-weekday'}
                      onChange={(e) => setCustomNthPosition(parseInt(e.target.value) as NthWeekdayPosition)}
                      className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none cursor-pointer disabled:opacity-50">
                      {NTH_POSITIONS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={customNthWeekday}
                      disabled={customMonthlyMode !== 'nth-weekday'}
                      onChange={(e) => setCustomNthWeekday(parseInt(e.target.value) as IsoWeekday)}
                      className="text-sm bg-transparent border border-border rounded-md px-2 py-1 outline-none cursor-pointer disabled:opacity-50">
                      {NTH_WEEKDAY_OPTIONS.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}

              {/* End condition */}
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Ends</span>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="end-kind"
                    checked={customEndKind === 'never'}
                    onChange={() => setCustomEndKind('never')}
                    className="accent-primary"
                  />
                  <span>Never</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="end-kind"
                    checked={customEndKind === 'on'}
                    onChange={() => setCustomEndKind('on')}
                    className="accent-primary"
                  />
                  <span className="text-muted-foreground">On</span>
                  <input
                    type="date"
                    value={customEndDate}
                    disabled={customEndKind !== 'on'}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    onBlur={applyCustomRule}
                    className="text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40 disabled:opacity-50"
                  />
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="end-kind"
                    checked={customEndKind === 'after'}
                    onChange={() => setCustomEndKind('after')}
                    className="accent-primary"
                  />
                  <span className="text-muted-foreground">After</span>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={customEndAfter}
                    disabled={customEndKind !== 'after'}
                    onChange={(e) => setCustomEndAfter(Math.max(1, parseInt(e.target.value) || 1))}
                    onBlur={applyCustomRule}
                    inputMode="numeric"
                    className="w-14 text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none text-center focus:border-primary/40 disabled:opacity-50"
                  />
                  <span className="text-muted-foreground">occurrences</span>
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <Separator />

        {/* Footer: time toggles per date + clear */}
        <div className="px-3 py-2.5 flex flex-col gap-2">
          {startDate && onHasStartTimeChange && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-10 shrink-0">Start</span>
              <button
                type="button"
                onClick={handleToggleStartTime}
                className={cn(
                  'flex items-center gap-1.5 rounded-md p-1.5 text-sm transition-colors',
                  hasStartTime
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}>
                <Clock className="h-4 w-4" />
              </button>
              {hasStartTime ? (
                <input
                  type="time"
                  value={startTimeValue}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className="text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40"
                />
              ) : null}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {startDate && onHasStartTimeChange ? (
                <span className="text-xs text-muted-foreground w-10 shrink-0">Due</span>
              ) : null}
              <button
                type="button"
                onClick={handleToggleTime}
                className={cn(
                  'flex items-center gap-1.5 rounded-md p-1.5 text-sm transition-colors',
                  hasDueTime
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}>
                <Clock className="h-4 w-4" />
              </button>
              {hasDueTime ? (
                <input
                  type="time"
                  value={timeValue}
                  onChange={(e) => handleTimeChange(e.target.value)}
                  className="text-sm pointer-coarse:text-base bg-transparent border border-border rounded-md px-2 py-1 outline-none focus:border-primary/40"
                />
              ) : null}
            </div>

            {!dueDateRequired && (
              <button
                type="button"
                onClick={handleClear}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Clear
              </button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
